import { useMemo } from 'react';
import { LinkOrText } from '../link-or-text';
import { BreakdownCell } from './interest-cells';
import {
  buildAttributeRows,
  wellHref,
  type AttrRow,
  type EconomicsPair,
} from './property-attributes';
import { resolveRuleColor, type ColorRule } from './color-by';
import type { ComparisonResult } from './types';
import style from './comparison-properties.module.scss';

interface ComparisonPropertiesTableProps {
  results: ComparisonResult[];
  /** true = every attribute row; false = only differing rows. */
  expandAll: boolean;
  /** Enabled color rules that apply to property tables (drives the color dot). */
  tableRules: ColorRule[];
  /** Both sides' scenario economics, for the per-unit economic rows. */
  economics: EconomicsPair | null;
}

// One side's melted-cell content: the LOE breakdown, or a plain (optionally
// linked) value with the Target-side delta chip.
const MeltValue = ({ attr, side }: { attr: AttrRow; side: 'a' | 'b' }) => {
  if (attr.breakdown) return <BreakdownCell row={attr} side={side} />;
  const href = side === 'a' ? attr.aHref : attr.bHref;
  const text = side === 'a' ? attr.aText : attr.bText;
  return (
    <>
      <LinkOrText href={href} className={style.link}>
        {text}
      </LinkOrText>
      {side === 'b' && attr.delta && (
        <span
          className={
            attr.delta.positive ? style.deltaPositive : style.deltaNegative
          }
        >
          {' '}
          [{attr.delta.text}]
        </span>
      )}
    </>
  );
};

/**
 * Table view of the Properties section (mock v16): a flat, "melted" table with
 * one row per differing attribute — columns Property ID | Property Name |
 * Attribute | Origin | Target. The target cell highlights and carries the
 * green/red delta chip, matching the cards.
 * @param {ComparisonPropertiesTableProps} props - The comparison results
 * @return {JSX.Element} The melted comparison table
 */
export const ComparisonPropertiesTable = ({
  results,
  expandAll,
  tableRules,
  economics,
}: ComparisonPropertiesTableProps) => {
  // Rebuilding every property's attribute rows is O(properties × attributes);
  // memoize so an unrelated re-render (search keystroke, color-rule toggle)
  // doesn't recompute them. `economics` is a stable reference from comparison-tool.
  const rows = useMemo(
    () =>
      results.flatMap((result) =>
        buildAttributeRows(result, economics)
          .filter((attr) => expandAll || attr.differs)
          .map((attr) => ({ result, attr }))
      ),
    [results, economics, expandAll]
  );

  return (
    <div className={style.tableWrapper}>
      <table className={style.meltTable}>
        <thead>
          <tr>
            <th scope="col" className={style.meltHead}>
              Property ID
            </th>
            <th scope="col" className={style.meltHead}>
              Property Name
            </th>
            <th scope="col" className={style.meltHead}>
              Attribute
            </th>
            <th scope="col" className={style.meltHead}>
              Origin
            </th>
            <th scope="col" className={style.meltHead}>
              Target
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map(({ result, attr }) => (
              <tr key={`${result.uniqueSlotId}-${attr.key}`}>
                <td className={style.meltCell}>
                  {(() => {
                    const dotColor = resolveRuleColor(result, tableRules);
                    return dotColor ? (
                      <span
                        className={style.colorDot}
                        style={{ backgroundColor: dotColor }}
                        aria-hidden="true"
                      />
                    ) : null;
                  })()}
                  <LinkOrText
                    href={result.uwi ? wellHref(result.uwi) : null}
                    className={style.link}
                  >
                    {result.uwi ?? result.uniqueSlotId}
                  </LinkOrText>
                </td>
                <td className={style.meltCell}>{result.wellName ?? ''}</td>
                <td className={style.meltCell}>{attr.label}</td>
                <td className={style.meltCell}>
                  <MeltValue attr={attr} side="a" />
                </td>
                <td
                  className={`${style.meltCell} ${
                    attr.differs ? style.diff : ''
                  }`}
                >
                  <MeltValue attr={attr} side="b" />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className={style.meltCell} colSpan={5}>
                No differing attributes.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
